import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Button, Card, Form, Input, Space, Typography, message } from "antd";
import {
  CheckCircleOutlined,
  LockOutlined,
  MailOutlined,
  UserOutlined,
  WalletOutlined,
  BarChartOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { confirmResetPassword, resetPassword } from "../../services/clientService";
import logoEnh from "../images/ENH.png";
import "./passwordReset.css";

const { Text, Title } = Typography;

function getApiError(error, fallback) {
  const data = error?.response?.data;
  const status = error?.response?.status;

  if (typeof data === "string") return data;
  if (data?.detail) return data.detail;
  if (data?.error?.message) return data.error.message;
  if (data?.error?.details?.detail?.message) return data.error.details.detail.message;
  if (data?.password?.[0]) return data.password[0];
  if (data?.token?.[0]) return data.token[0];
  if (data?.email?.[0]) return data.email[0];
  if (status === 404) {
    return "Token inválido ou expirado. Solicite um novo link de reposição.";
  }

  return fallback;
}

export default function PasswordResetPage() {
  const history = useHistory();
  const location = useLocation();
  const [requestForm] = Form.useForm();
  const [confirmForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  const tokenFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token") || "";
  }, [location.search]);

  const hasToken = Boolean(tokenFromUrl);

  const requestLink = async (values) => {
    setLoading(true);
    try {
      await resetPassword({ email: values.email.trim() });
      setRequestSent(true);
      message.success("Enviámos o link para o email indicado.");
      requestForm.resetFields();
    } catch (error) {
      message.error(getApiError(error, "Não foi possível enviar o email de reposição."));
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (values) => {
    setLoading(true);
    try {
      await confirmResetPassword({
        token: tokenFromUrl,
        password: values.password,
      });
      setPasswordChanged(true);
      message.success("Password definida com sucesso.");
      confirmForm.resetFields();
    } catch (error) {
      message.error(getApiError(error, "Token inválido, expirado ou password não aceite."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="password-reset-page">
      <section className="password-reset-shell">
        <aside className="password-reset-brand">
          <div className="password-reset-brand-content">
            <div className="password-reset-logo-wrap">
              <img src={logoEnh} alt="ENH KOGAS" className="password-reset-logo-large" />
            </div>
            <Title level={1}>Sistema Pre-pago ENH KOGAS</Title>
            <Text className="password-reset-copy">
              Reposicao segura de acesso para o portal interno de clientes,
              pagamentos e recargas.
            </Text>

            <div className="password-reset-features">
              <div className="password-reset-feature">
                <SafetyCertificateOutlined />
                <span>Ligacao segura por token</span>
              </div>
              <div className="password-reset-feature">
                <UserOutlined />
                <span>Acesso individual de utilizador</span>
              </div>
              <div className="password-reset-feature">
                <WalletOutlined />
                <span>Proteccao de operacoes pre-pagas</span>
              </div>
              <div className="password-reset-feature">
                <BarChartOutlined />
                <span>Portal interno ENH KOGAS</span>
              </div>
            </div>
          </div>

          <div className="password-reset-brand-footer">
            <p>Portal interno ENH KOGAS - Sistema Pre-pago</p>
          </div>
        </aside>

        <Card className="password-reset-card" bordered={false}>
          {passwordChanged ? (
            <div className="password-reset-result">
              <CheckCircleOutlined className="password-reset-success-icon" />
              <Title level={2}>Password actualizada</Title>
              <Text>
                Já pode entrar no MPS com a nova password.
              </Text>
              <Button type="primary" size="large" onClick={() => history.push("/")}>
                Ir para login
              </Button>
            </div>
          ) : hasToken ? (
            <>
              <div className="password-reset-form-header">
                <img src={logoEnh} alt="ENH KOGAS" className="password-reset-logo-small" />
              </div>

              <Space direction="vertical" size={4} className="password-reset-heading">
                <Text className="password-reset-eyebrow">Definir password</Text>
                <Title level={2}>Crie a sua nova password</Title>
                <Text type="secondary">Use uma password privada, com pelo menos 8 caracteres.</Text>
              </Space>

              <Form
                form={confirmForm}
                layout="vertical"
                requiredMark={false}
                onFinish={changePassword}
              >
                <Form.Item
                  label="Nova password"
                  name="password"
                  rules={[
                    { required: true, message: "Introduza a nova password." },
                    { min: 8, message: "A password deve ter pelo menos 8 caracteres." },
                  ]}
                  hasFeedback
                >
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined />}
                    autoComplete="new-password"
                    placeholder="Nova password"
                  />
                </Form.Item>

                <Form.Item
                  label="Confirmar password"
                  name="confirmPassword"
                  dependencies={["password"]}
                  hasFeedback
                  rules={[
                    { required: true, message: "Confirme a nova password." },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("As passwords não coincidem."));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined />}
                    autoComplete="new-password"
                    placeholder="Repita a password"
                  />
                </Form.Item>

                <Button type="primary" htmlType="submit" size="large" block loading={loading}>
                  Guardar nova password
                </Button>
              </Form>
            </>
          ) : (
            <>
              <div className="password-reset-form-header">
                <img src={logoEnh} alt="ENH KOGAS" className="password-reset-logo-small" />
              </div>

              <Space direction="vertical" size={4} className="password-reset-heading">
                <Text className="password-reset-eyebrow">Reposição de acesso</Text>
                <Title level={2}>Receber link por email</Title>
                <Text type="secondary">
                  Indique o email associado ao seu utilizador. Enviaremos um link seguro para definir a password.
                </Text>
              </Space>

              {requestSent && (
                <Alert
                  type="success"
                  showIcon
                  message="Email enviado"
                  description="Verifique a sua caixa de entrada e também o spam, caso o email demore a aparecer."
                  className="password-reset-alert"
                />
              )}

              <Form
                form={requestForm}
                layout="vertical"
                requiredMark={false}
                onFinish={requestLink}
              >
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: "Introduza o email." },
                    { type: "email", message: "Introduza um email válido." },
                  ]}
                >
                  <Input
                    size="large"
                    prefix={<MailOutlined />}
                    autoComplete="email"
                    placeholder="email@empresa.co.mz"
                  />
                </Form.Item>

                <Button type="primary" htmlType="submit" size="large" block loading={loading}>
                  Enviar link de reposição
                </Button>
              </Form>
            </>
          )}
        </Card>
      </section>
    </main>
  );
}
